import React, { useState } from 'react';
import { Card } from '../SubscriptionAppCard';
import { Button } from '../SubscriptionAppButton';
import { Calendar } from '../BulkActionsManager/components/Calendar';
import { ChevronDown, ChevronRight, ShoppingBag, CreditCard, ArrowRight, Wine, Calendar as CalendarIcon } from 'lucide-react';
interface Order {
  id: string;
  date: string;
  orderId: string;
  chargeId: string;
  amount: string;
  status: 'success' | 'pending' | 'failed';
  isCheckout?: boolean;
  items?: Array<{
    name: string;
    quantity: number;
    price: string;
  }>;
  subtotal?: string;
  taxes?: string;
  shipping?: string;
  shippingType?: string;
  total?: string;
  chargeCreatedAt?: string;
  paymentProcessor?: string;
  shippingAddress?: {
    name: string;
    street: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}
export interface OrdersTableProps {
  upcomingOrders?: Order[];
  recentOrders?: Order[];
  'data-id'?: string;
}
export function OrdersTable({
  upcomingOrders = [],
  recentOrders = [],
  'data-id': dataId
}: OrdersTableProps) {
  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const [recentExpanded, setRecentExpanded] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-[#10B981]';
      case 'pending':
        return 'text-[#F59E0B]';
      case 'failed':
        return 'text-[#EF4444]';
      default:
        return 'text-[#94A3B8]';
    }
  };
  const getStatusDot = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-[#10B981]';
      case 'pending':
        return 'bg-[#F59E0B]';
      case 'failed':
        return 'bg-[#EF4444]';
      default:
        return 'bg-[#94A3B8]';
    }
  };
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };
  const handleReschedule = (orderId: string) => {
    setShowDatePicker(showDatePicker === orderId ? null : orderId);
  };
  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };
  const handleSaveDate = (e: React.MouseEvent, orderId: string) => {
    e.stopPropagation();
    // Here you would typically save the date to your backend
    console.log('Saving new date:', selectedDate, 'for order:', orderId);
    setShowDatePicker(null);
  };
  return <Card padding="none" data-id="element-1872">
      {/* Upcoming Orders Section */}
      <div className="border-b border-[#E2E8F0]" data-id="element-1873">
        <button onClick={() => setUpcomingExpanded(!upcomingExpanded)} className="w-full flex items-center gap-2 p-6 hover:bg-[#F8FAFC] transition-colors" data-id="element-1874">
          {upcomingExpanded ? <ChevronDown className="w-5 h-5 text-[#475569]" data-id="element-1875" /> : <ChevronRight className="w-5 h-5 text-[#475569]" data-id="element-1876" />}
          <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-1877">
            Upcoming orders
          </h3>
        </button>

        {upcomingExpanded && <div className="px-6 pb-6" data-id="element-1878">
            <div className="space-y-2" data-id="element-1879">
              {upcomingOrders.map(order => <div key={order.id} className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-1880">
                  {/* Order Row */}
                  <div className="flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={() => toggleOrderExpansion(order.id)} data-id="element-1881">
                    <div className="flex items-center gap-4" data-id="element-1882">
                      <button className="text-[#475569] hover:text-[#0EA5E9]" data-id="element-1883">
                        {expandedOrderId === order.id ? <ChevronDown className="w-4 h-4" data-id="element-1884" /> : <ChevronRight className="w-4 h-4" data-id="element-1885" />}
                      </button>
                      <span className="text-sm text-[#0F172A]" data-id="element-1886">
                        {order.date}
                      </span>
                    </div>

                    <div className="flex items-center gap-6" data-id="element-1887">
                      <div className="flex items-center gap-2" data-id="element-1888">
                        <ShoppingBag className="w-4 h-4 text-[#10B981]" data-id="element-1889" />
                        <span className="text-sm font-medium text-[#0EA5E9]" data-id="element-1890">
                          {order.orderId}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1891">
                        <CreditCard className="w-4 h-4 text-[#6F42C1]" data-id="element-1892" />
                        <span className="text-sm text-[#475569]" data-id="element-1893">
                          {order.chargeId}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#0F172A] min-w-[100px] text-right" data-id="element-1894">
                        {order.amount}
                      </span>
                      <div className="flex items-center gap-2 min-w-[80px]" data-id="element-1895">
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(order.status)}`} data-id="element-1896"></div>
                        <span className={`text-sm font-medium capitalize ${getStatusColor(order.status)}`} data-id="element-1897">
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedOrderId === order.id && order.items && <div className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-id="element-1898">
                      <div className="p-6 grid grid-cols-2 gap-6" data-id="element-1899">
                        {/* Left Column - Charge Summary */}
                        <div className="space-y-4" data-id="element-1900">
                          <div data-id="element-1901">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-3" data-id="element-1902">
                              Charge Summary
                            </h4>
                            {order.items.map((item, idx) => <div key={idx} className="flex gap-3 mb-4" data-id="element-1903">
                                <div className="w-12 h-12 bg-white border border-[#E2E8F0] rounded flex items-center justify-center flex-shrink-0" data-id="element-1904">
                                  <Wine className="w-6 h-6 text-[#6F42C1]" data-id="element-1905" />
                                </div>
                                <div className="flex-1" data-id="element-1906">
                                  <div className="font-medium text-[#0F172A] text-sm" data-id="element-1907">
                                    {item.name}
                                  </div>
                                </div>
                                <div className="text-right" data-id="element-1908">
                                  <div className="text-sm text-[#0F172A]" data-id="element-1909">
                                    {item.quantity} x {item.price}
                                  </div>
                                </div>
                              </div>)}
                          </div>

                          {/* Totals */}
                          <div className="space-y-2 pt-4 border-t border-[#E2E8F0]" data-id="element-1910">
                            <div className="flex justify-between text-sm" data-id="element-1911">
                              <span className="text-[#475569]" data-id="element-1912">Subtotal</span>
                              <span className="text-[#475569]" data-id="element-1913">
                                {order.items.length}{' '}
                                {order.items.length === 1 ? 'item' : 'items'}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-1914">
                                {order.subtotal}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-1915">
                              <span className="text-[#475569]" data-id="element-1916">Taxes</span>
                              <span className="text-[#475569]" data-id="element-1917"></span>
                              <span className="text-[#0F172A]" data-id="element-1918">
                                {order.taxes}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-1919">
                              <span className="text-[#475569]" data-id="element-1920">Shipping</span>
                              <span className="text-[#475569]" data-id="element-1921">
                                {order.shippingType}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-1922">
                                {order.shipping}
                              </span>
                            </div>
                            <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#E2E8F0]" data-id="element-1923">
                              <span className="text-[#0F172A]" data-id="element-1924">Total</span>
                              <span className="text-[#0F172A]" data-id="element-1925">
                                {order.total}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Charge Details */}
                        <div className="space-y-4" data-id="element-1926">
                          {/* Charge Type */}
                          <Card padding="sm" data-id="element-1927">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-1928">
                              Charge Type
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-1929">
                              Recurring
                            </div>
                          </Card>

                          {/* Scheduled Date with Reschedule */}
                          <Card padding="sm" data-id="element-1930">
                            <div className="flex items-center justify-between mb-2" data-id="element-1931">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider" data-id="element-1932">
                                Scheduled Date
                              </h4>
                              <Button variant="ghost" size="sm" iconLeft={<CalendarIcon className="w-3 h-3" data-id="element-1934" />} onClick={e => {
                        e.stopPropagation();
                        handleReschedule(order.id);
                      }} className="h-auto py-1 px-2 min-w-0 text-xs" data-id="element-1933">
                                Reschedule
                              </Button>
                            </div>
                            <div className="text-sm text-[#0F172A]" data-id="element-1935">
                              {order.date}
                            </div>

                            {/* Date Picker */}
                            {showDatePicker === order.id && <div className="mt-3 pt-3 border-t border-[#E2E8F0]" data-id="element-1936">
                                <Calendar value={selectedDate} onChange={handleDateChange} minDate={new Date()} includeTime={true} data-id="element-1937" />
                                <div className="flex gap-2 mt-2" data-id="element-1938">
                                  <Button size="sm" onClick={e => handleSaveDate(e, order.id)} className="flex-1" data-id="element-1939">
                                    Save
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={e => {
                          e.stopPropagation();
                          setShowDatePicker(null);
                        }} className="flex-1" data-id="element-1940">
                                    Cancel
                                  </Button>
                                </div>
                              </div>}
                          </Card>

                          {/* Charge Created At */}
                          {order.chargeCreatedAt && <Card padding="sm" data-id="element-1941">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-1942">
                                Charge Created At
                              </h4>
                              <div className="text-sm text-[#0F172A]" data-id="element-1943">
                                {order.chargeCreatedAt}
                              </div>
                            </Card>}

                          {/* Order Status */}
                          <Card padding="sm" data-id="element-1944">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-1945">
                              Order Status
                            </h4>
                            <div className="text-sm text-[#F59E0B] capitalize" data-id="element-1946">
                              {order.status}
                            </div>
                          </Card>

                          {/* Payment Processor */}
                          <Card padding="sm" data-id="element-1947">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-1948">
                              Payment Processor
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-1949">
                              {order.paymentProcessor}
                            </div>
                          </Card>

                          {/* Shipping Address */}
                          {order.shippingAddress && <Card padding="sm" data-id="element-1950">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-1951">
                                Shipping Address
                              </h4>
                              <div className="text-sm text-[#0F172A] space-y-0.5" data-id="element-1952">
                                <div data-id="element-1953">{order.shippingAddress.name}</div>
                                <div data-id="element-1954">{order.shippingAddress.street}</div>
                                {order.shippingAddress.street2 && <div data-id="element-1955">{order.shippingAddress.street2}</div>}
                                <div data-id="element-1956">
                                  {order.shippingAddress.city},{' '}
                                  {order.shippingAddress.state}{' '}
                                  {order.shippingAddress.zip}
                                </div>
                                <div data-id="element-1957">{order.shippingAddress.country}</div>
                              </div>
                            </Card>}
                        </div>
                      </div>
                    </div>}
                </div>)}
            </div>
          </div>}
      </div>

      {/* Recent Orders Section */}
      <div data-id="element-1958">
        <button onClick={() => setRecentExpanded(!recentExpanded)} className="w-full flex items-center gap-2 p-6 hover:bg-[#F8FAFC] transition-colors" data-id="element-1959">
          {recentExpanded ? <ChevronDown className="w-5 h-5 text-[#475569]" data-id="element-1960" /> : <ChevronRight className="w-5 h-5 text-[#475569]" data-id="element-1961" />}
          <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-1962">
            Recent orders
          </h3>
        </button>

        {recentExpanded && <div className="px-6 pb-6" data-id="element-1963">
            <div className="space-y-2" data-id="element-1964">
              {recentOrders.map(order => <div key={order.id} className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-1965">
                  {/* Order Row */}
                  <div className="flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={() => toggleOrderExpansion(order.id)} data-id="element-1966">
                    <div className="flex items-center gap-4" data-id="element-1967">
                      <button className="text-[#475569] hover:text-[#0EA5E9]" data-id="element-1968">
                        {expandedOrderId === order.id ? <ChevronDown className="w-4 h-4" data-id="element-1969" /> : <ChevronRight className="w-4 h-4" data-id="element-1970" />}
                      </button>
                      <div className="flex items-center gap-3" data-id="element-1971">
                        <span className="text-sm text-[#0F172A]" data-id="element-1972">
                          {order.date}
                        </span>
                        {order.isCheckout && <span className="px-2 py-0.5 bg-[#0F172A] text-white text-xs font-medium rounded" data-id="element-1973">
                            Checkout
                          </span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-6" data-id="element-1974">
                      <div className="flex items-center gap-2" data-id="element-1975">
                        <ShoppingBag className="w-4 h-4 text-[#10B981]" data-id="element-1976" />
                        <span className="text-sm font-medium text-[#0EA5E9]" data-id="element-1977">
                          {order.orderId}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1978">
                        <CreditCard className="w-4 h-4 text-[#6F42C1]" data-id="element-1979" />
                        <span className="text-sm text-[#475569]" data-id="element-1980">
                          {order.chargeId}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#0F172A] min-w-[100px] text-right" data-id="element-1981">
                        {order.amount}
                      </span>
                      <div className="flex items-center gap-2 min-w-[80px]" data-id="element-1982">
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(order.status)}`} data-id="element-1983"></div>
                        <span className={`text-sm font-medium capitalize ${getStatusColor(order.status)}`} data-id="element-1984">
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedOrderId === order.id && order.items && <div className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-id="element-1985">
                      <div className="p-6 grid grid-cols-2 gap-6" data-id="element-1986">
                        {/* Left Column - Charge Summary */}
                        <div className="space-y-4" data-id="element-1987">
                          <div data-id="element-1988">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-3" data-id="element-1989">
                              Charge Summary
                            </h4>
                            {order.items.map((item, idx) => <div key={idx} className="flex gap-3 mb-4" data-id="element-1990">
                                <div className="w-12 h-12 bg-white border border-[#E2E8F0] rounded-md flex items-center justify-center flex-shrink-0" data-id="element-1991">
                                  <Wine className="w-6 h-6 text-[#6F42C1]" data-id="element-1992" />
                                </div>
                                <div className="flex-1" data-id="element-1993">
                                  <div className="font-medium text-[#0F172A] text-sm" data-id="element-1994">
                                    {item.name}
                                  </div>
                                </div>
                                <div className="text-right" data-id="element-1995">
                                  <div className="text-sm text-[#0F172A]" data-id="element-1996">
                                    {item.quantity} x {item.price}
                                  </div>
                                </div>
                              </div>)}
                          </div>

                          {/* Totals */}
                          <div className="space-y-2 pt-4 border-t border-[#E2E8F0]" data-id="element-1997">
                            <div className="flex justify-between text-sm" data-id="element-1998">
                              <span className="text-[#475569]" data-id="element-1999">Subtotal</span>
                              <span className="text-[#475569]" data-id="element-2000">
                                {order.items.length}{' '}
                                {order.items.length === 1 ? 'item' : 'items'}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-2001">
                                {order.subtotal}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-2002">
                              <span className="text-[#475569]" data-id="element-2003">Taxes</span>
                              <span className="text-[#475569]" data-id="element-2004"></span>
                              <span className="text-[#0F172A]" data-id="element-2005">
                                {order.taxes}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-2006">
                              <span className="text-[#475569]" data-id="element-2007">Shipping</span>
                              <span className="text-[#475569]" data-id="element-2008">
                                {order.shippingType}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-2009">
                                {order.shipping}
                              </span>
                            </div>
                            <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#E2E8F0]" data-id="element-2010">
                              <span className="text-[#0F172A]" data-id="element-2011">Total</span>
                              <span className="text-[#0F172A]" data-id="element-2012">
                                {order.total}
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-4" data-id="element-2013">
                            <Button variant="ghost" size="sm" data-id="element-2014">
                              Refund in Shopify
                            </Button>
                          </div>
                        </div>

                        {/* Right Column - Charge Details */}
                        <div className="space-y-4" data-id="element-2015">
                          {/* Charge Type */}
                          <Card padding="sm" data-id="element-2016">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-2017">
                              Charge Type
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-2018">
                              {order.isCheckout ? 'Checkout' : 'Recurring'}
                            </div>
                          </Card>

                          {/* Charge ID */}
                          <Card padding="sm" data-id="element-2019">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-2020">
                              Charge ID
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-2021">
                              {order.chargeId.replace('#', '')}
                            </div>
                          </Card>

                          {/* Charge Created At */}
                          <Card padding="sm" data-id="element-2022">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-2023">
                              Charge Created At
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-2024">
                              {order.chargeCreatedAt}
                            </div>
                          </Card>

                          {/* Order Status */}
                          <Card padding="sm" data-id="element-2025">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-2026">
                              Order Status
                            </h4>
                            <div className="text-sm text-[#10B981] capitalize" data-id="element-2027">
                              {order.status}
                            </div>
                          </Card>

                          {/* Payment Processor */}
                          <Card padding="sm" data-id="element-2028">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-2029">
                              Payment Processor
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-2030">
                              {order.paymentProcessor}
                            </div>
                          </Card>

                          {/* Shipping Address */}
                          {order.shippingAddress && <Card padding="sm" data-id="element-2031">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-2032">
                                Shipping Address
                              </h4>
                              <div className="text-sm text-[#0F172A] space-y-0.5" data-id="element-2033">
                                <div data-id="element-2034">{order.shippingAddress.name}</div>
                                <div data-id="element-2035">{order.shippingAddress.street}</div>
                                {order.shippingAddress.street2 && <div data-id="element-2036">{order.shippingAddress.street2}</div>}
                                <div data-id="element-2037">
                                  {order.shippingAddress.city},{' '}
                                  {order.shippingAddress.state}{' '}
                                  {order.shippingAddress.zip}
                                </div>
                                <div data-id="element-2038">{order.shippingAddress.country}</div>
                              </div>
                            </Card>}
                        </div>
                      </div>

                      {/* See Details Link */}
                      <div className="px-6 pb-6" data-id="element-2039">
                        <Button variant="ghost" size="sm" iconRight={<ArrowRight className="w-4 h-4" data-id="element-2041" />} data-id="element-2040">
                          View in Shopify
                        </Button>
                      </div>
                    </div>}
                </div>)}
            </div>

            {/* View All Button */}
            <div className="mt-4 flex justify-end" data-id="element-2042"></div>
          </div>}
      </div>
    </Card>;
}